import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEOMeta from '@/components/base/SEOMeta';
import StructuredData from '@/components/base/StructuredData';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <span className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-6">
              <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
            </span>
            <h1 className="text-2xl font-bold text-foreground-950 mb-3 font-heading">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料大全',
      logo: {
        '@type': 'ImageObject',
        url: 'https://heiliao.com/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://heiliao.com/article/${article.id}`,
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${article.title} - 黑料大全`,
    description: article.excerpt,
    url: `https://heiliao.com/article/${article.id}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '黑料大全',
      url: 'https://heiliao.com',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliao.com' },
        { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://heiliao.com/category/${article.category}` },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    },
  };

  return (
    <>
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://heiliao.com/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
        ogImageAlt={article.title}
      />
      <StructuredData data={articleSchema} />
      <StructuredData data={webPageSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            {/* Article Header */}
            <header className="py-6 md:py-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {article.categoryName}
                </Link>
                {article.isHot && (
                  <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                    热门
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 mb-4 font-heading leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.viewCount >= 10000
                    ? `${(article.viewCount / 10000).toFixed(1)}万 阅读`
                    : `${article.viewCount} 阅读`}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-chat-3-line"></i>
                  {article.commentCount >= 10000
                    ? `${(article.commentCount / 10000).toFixed(1)}万 评论`
                    : `${article.commentCount} 评论`}
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="rounded-xl overflow-hidden mb-8 bg-background-200">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 黑料大全`}
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Article Content */}
            <article
              className="prose-custom mb-10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap mb-10 pt-6 border-t border-background-200/70">
              <span className="text-sm font-semibold text-foreground-700 whitespace-nowrap">
                <i className="ri-price-tag-3-line mr-1"></i>
                标签：
              </span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full text-xs bg-background-100 border border-background-200/70 text-foreground-600 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Share */}
            <div className="flex items-center gap-3 mb-12">
              <span className="text-sm font-semibold text-foreground-700 whitespace-nowrap">分享到：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/70 text-foreground-600 hover:text-primary-600 hover:border-primary-200 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/70 text-foreground-600 hover:text-primary-600 hover:border-primary-200 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/70 text-foreground-600 hover:text-primary-600 hover:border-primary-200 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-twitter-x-line"></i>
              </button>
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full bg-background-100 py-10 md:py-14">
              <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
                <h3 className="text-xl font-bold text-foreground-950 mb-6 font-heading">
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}